/**
 * Spinner — the shared page spinner, drawn as the working mark (Justin, 2026-09-26, design record D-71: every spinner
 * on prest0.ai is the logo's zero, turning; release 2 carries it past the chat).
 *
 * It renders the one element <MarkSpinner> renders (./markSpinnerMarkup.ts), drawn by the one stylesheet
 * (src/styles/markSpinnerCss.ts) and turned on the one clock (src/utils/markSpinnerPhase.ts), and it keeps the name and
 * props the pages already use — the branded transition, sign-in, the invite page, onboarding, redeem and the
 * workspace-switch overlay — so none of them had to change:
 *   - `$size`: the room it has, in px. The mark takes the largest of its five sizes that fits (never above 24, the
 *     design system's cap; the smallest, 10, when none does). Left out, it is the largest — the old ring was 28 px.
 *   - `$onDark`: a surface dark in every light (the workspace-switch overlay). The mark takes its night inks — the cream
 *     ink and the logo's own satin — which its boot-splash twin in public/index.html draws too
 *     (tests/workspaces/splashBootContinuity.test.ts pins the pair).
 *
 * It carries no styles of its own, so it is a plain component; decorative, like every mark (the words beside it say
 * what is happening).
 */
import React from 'react';
import { MarkSpinner } from './MarkSpinner';
import { MARK_INKS_ON_DARK, MARK_SPINNER_SIZES_PX, type MarkSpinnerSize } from './markSpinnerMarkup';

const SIZES_SMALLEST_FIRST = (Object.entries(MARK_SPINNER_SIZES_PX) as [MarkSpinnerSize, number][])
  .sort((a, b) => a[1] - b[1]);

/** The mark's size for the room a caller gives it: the largest of the five that fits, the smallest when none does. */
export function markSizeFor(px?: number): MarkSpinnerSize {
  if (px === undefined || !Number.isFinite(px)) return SIZES_SMALLEST_FIRST[SIZES_SMALLEST_FIRST.length - 1][0];
  let fit = SIZES_SMALLEST_FIRST[0][0];
  for (const [size, width] of SIZES_SMALLEST_FIRST) if (width <= px) fit = size;
  return fit;
}

/** The night inks as a style: the four properties the one stylesheet reads. */
const ON_DARK = MARK_INKS_ON_DARK as unknown as React.CSSProperties;

export interface SpinnerProps {
  /** The room the spinner has, in px (default: the largest size, 24). */
  $size?: number;
  /** The surface is dark in every light: the mark's night inks. */
  $onDark?: boolean;
  className?: string;
}

export const Spinner: React.FC<SpinnerProps> = ({ $size, $onDark, className }) => (
  <MarkSpinner size={markSizeFor($size)} className={className} style={$onDark ? ON_DARK : undefined} />
);

export default Spinner;
